import * as React from "react";
import { merge, $ } from "glamor";
import { theme } from "styles/theme";
import debounce from "lodash/debounce";
import { formatMessage } from "lib/globalize-util";
import { Message } from "./message";
import CircularProgress from "material-ui/CircularProgress";
import { List, Map } from "immutable";
import { VIEWPORT_MOBILE, VIEWPORT_PHABLET } from "constants/media";
import { DATATEST } from "constants/integration";
interface IProps {
    app: any;
    me: any;
    collectionId: string;
    chatState: any;
    messages: any;
    onOpenDeleteDialog: any;
    onGetCollectionChatMessages: any;
    onResetChatAlert: () => void;
    bindListReference: (ref: any) => void;
    activeUserSessions: Map<string, any>;
    editing: boolean;
}

export class Messages extends React.PureComponent<IProps, any> {
    $list: any;

    styles = merge(
        {
            flexGrow: 1,
            flexShrink: 1,
            overflowY: "auto",
            height: "20px",
        },
        $(" .message", {
            whiteSpace: "pre-line",
            width: "inherit !important",
            borderRadius: "5px 0px 5px 5px",
            marginLeft: "10px",
            position: "relative",
        }),
        $(" .message.owned", {
            right: "0px !important",
            left: "inherit !important",
            marginRight: "10px",
        }),
        $(" .messageContent", {
            position: "relative",
            marginLeft: "35px !important",
            float: "left",
            backgroundColor: `${theme.palette.lightestGrey} !important`,
        }),
        $(" .messageContent p", {
            userSelect: "all",
        }),
        $(" .messageContent.owned", {
            paddingRight: "60px !important",
            backgroundColor: "#cfd8dc !important",
            marginLeft: "0px !important",
            float: "right",
        }),
        $(" .messageInstructions", {
            borderRadius: "4px",
            textAlign: "center",
            padding: "6px",
            height: "30px !important",
        }),
        $(" .beginningOfHistory", {
            backgroundColor: "transparent",
            color: theme.palette.grey,
            flex: "1 1 auto",
        }),
        $(" p", {
            fontSize: "12px",
            wordBreak: "break-word",
            hyphens: "auto",
        }),
        $(" .timestamp", {
            color: theme.palette.grey,
        }),
        $(" .timestamp.owned", {
            color: theme.palette.darkGrey,
        }),
        $(" .directional", {
            background: "none",
            overflow: "hidden",
            height: "20px",
            width: "15px",
            position: "absolute",
            top: "0px",
            bottom: "auto",
            right: "-9px",
            backgroundColor: "#cfd8dc",
        }),
        $(" .taper", {
            left: "6px",
            borderRadius: "50%",
            width: "21px",
            height: "32px",
            position: "absolute",
            top: "0px",
            backgroundColor: theme.palette.canvasColor,
        }),
        $(" .directionalAlt", {
            background: "none",
            overflow: "hidden",
            height: "20px",
            width: "15px",
            position: "absolute",
            top: "0px",
            bottom: "auto",
            left: "-13px",
            backgroundColor: theme.palette.lightestGrey,
        }),
        $(" .taperLeft", {
            left: "-9px",
            borderRadius: "50%",
            width: "21px",
            height: "32px",
            position: "absolute",
            top: "0px",
            backgroundColor: "#f7f7f7",
        }),
        $(" h3", {
            fontSize: "14px",
            margin: "0px 0 6px 0",
            fontWeight: "bold",
        }),
        $(" .listWrapper", {
            whiteSpace: "pre-line",
            overflowY: "scroll",
            display: "flex",
            flexDirection: "column-reverse",
        }),
        $(" .listContainer", {
            overflowY: "auto",
            height: "100%",
            borderTop: `1px solid ${theme.palette.borderColor}`,
        }),
        $(" .listContent", {
            display: "flex",
            flexDirection: "column-reverse",
        })
    );

    muiStyles = {
        circularProgress: {
            width: "50px",
            margin: "0 auto",
            display: "block",
        },
    };

    constructor(props) {
        super(props);
        this.handleScrollList = debounce(this.handleScrollList, 300);
        this.state = {
            scrollHeight: 0,
        };
    }

    componentDidMount() {
        this.$list.addEventListener("scroll", this.handleScrollList, false);
        window.addEventListener("resize", this.handleWindowResize);
        this.$list.scrollTop = this.$list.scrollHeight;
        this.setState({
            originalHeight: this.getHeight(),
        });
    }

    componentWillUnmount() {
        this.$list.removeEventListener("scroll", this.handleScrollList, false);
        window.removeEventListener("resize", this.handleWindowResize);
        (this.handleScrollList as any).cancel();
    }

    UNSAFE_componentWillUpdate(nextProps) {
        const { app } = this.props;
        if (nextProps.messages !== this.props.messages) {
            const amtAdded = this.$list.scrollHeight - this.state.scrollHeight;
            const offset = [VIEWPORT_PHABLET, VIEWPORT_MOBILE].includes(app.viewport) ? 140 : 150;
            this.setState({
                scrollHeight: this.state.scrollHeight + amtAdded - offset,
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { me } = this.props;
        const myMessages = this.props.chatState.newMessages.filter((msg) => msg.identity === me._id);

        // In all but the first fetch, we need to scroll to where the next page begins
        if (prevProps.chatState.pageCount !== this.props.chatState.pageCount && this.props.chatState.pageCount !== 0) {
            this.$list.scrollTop = this.$list.scrollHeight - this.state.scrollHeight - 150;
        }

        // if we have received new polled messages
        if (prevProps.chatState.newMessages !== this.props.chatState.newMessages) {
            if (myMessages.length) {
                // we have successfully created a new message, scroll to bottom of chat
                this.$list.scrollTop = this.$list.scrollHeight;
            }
        }
    }

    handleWindowResize = () => {
        this.forceUpdate();
    };

    getHeight = () => {
        const { app } = this.props;
        let height = window.innerHeight - 150;
        if ([VIEWPORT_PHABLET, VIEWPORT_MOBILE].includes(app.viewport)) {
            height = window.innerHeight - 140;
        }

        return height;
    };

    fetchNextPage = () => {
        if (this.props.chatState.hasMore) {
            const lastMessage = this.props.messages.last();
            let options = {};
            if (lastMessage) {
                options = {
                    min_message_id: lastMessage.id,
                };
            }
            this.props.onGetCollectionChatMessages(this.props.collectionId, options);
        }
    };

    handleScrollList = (event) => {
        if (event.target.scrollTop === 0) {
            this.fetchNextPage();
        }
        if (event.target.scrollTop + window.innerHeight - 200 >= event.target.scrollHeight) {
            this.props.onResetChatAlert();
        }

        // auto-hide after real scroll
        setTimeout(this.props.onResetChatAlert, 2000);
    };

    bindList = (ref) => {
        this.$list = ref;
        this.props.bindListReference(ref);
    };

    handleSeeNewMessages = () => {
        this.$list.scrollTop = this.$list.scrollHeight;
        this.props.onResetChatAlert();
    };

    render() {
        const { me, messages, chatState, activeUserSessions } = this.props;

        let content = List([]);
        if (messages.size > 0) {
            content = messages.map((message, index) => {
                const session = activeUserSessions.get(message.identity);
                return (
                    <Message
                        key={index}
                        message={message}
                        onOpenDeleteDialog={this.props.onOpenDeleteDialog}
                        me={this.props.me}
                        index={index}
                        color={session && session.get("color")}
                    />
                );
            });
        }

        if (!chatState.hasMore && !chatState.isFetching) {
            const message = {
                id: "beginning",
                content: formatMessage("This is the start of chat history"),
            };
            content = content.push(
                <Message
                    key={-1}
                    message={message}
                    onOpenDeleteDialog={this.props.onOpenDeleteDialog}
                    me={this.props.me}
                    index={-1}
                />
            );
        }

        const unownedMessages = chatState.newMessages.filter((msg) => msg.identity !== me._id);
        return (
            <div {...this.styles}>
                {unownedMessages.length > 0 && (
                    <div className="alertWrapper" onClick={this.handleSeeNewMessages}>
                        <div className="newMessagesAlert">
                            {formatMessage("You have |count| new message(s)", { count: unownedMessages.length })}
                        </div>
                    </div>
                )}

                {chatState.isFetching && <CircularProgress style={this.muiStyles.circularProgress} />}

                <div data-test={DATATEST.ASSORTMENTS.CHAT_CONTAINER} className="listContainer" ref={this.bindList}>
                    <div className="listContent">{content}</div>
                </div>

                {/* <div
                    className="listWrapper"
                    style={{ maxHeight: this.getMaxHeight(), minHeight: this.getMaxHeight() }}
                    ref={this.bindList}
                >
                    {content}
                </div> */}
            </div>
        );
    }
}
